// Origin/path joining shared by every Notability web property's canonical and Open Graph URLs.

export function stripTrailingSlash(origin: string): string {
  return origin.replace(/\/$/, '')
}

export function absoluteUrl(origin: string, path: string): string {
  return `${stripTrailingSlash(origin)}${path.startsWith('/') ? path : `/${path}`}`
}

// Drops the query string and fragment, leaving just the path. Cuts at the first `?` or `#`,
// whichever comes first, so a fragment containing a `?` doesn't survive.
export function stripQueryAndHash(path: string): string {
  const marker = path.search(/[?#]/)
  return marker === -1 ? path : path.slice(0, marker)
}

// Localizes `router.asPath`, which strips the active locale (`router.locale`).
// That's problematic for e.g. producing canonical URLs, which should show localized results
// for searches from a given locale.
//
// `localePath(stripQueryAndHash(router.asPath), router.locale, router.defaultLocale)`
//
// Prefixing assumes sub-path locale routing — if the sites ever move to locale domains
// (`router.isLocaleDomain`), the locale belongs in the origin.
export function localePath(path: string, locale?: string, defaultLocale?: string): string {
  if (!locale || locale === defaultLocale) {
    return path
  }
  // Callers may pass an explicit path that already carries the prefix; don't double it. Locale tags
  // are mixed-case (`zh-Hans`, `id-ID`), so compare case-insensitively as Next does.
  const prefix = `/${locale.toLowerCase()}`
  const lower = path.toLowerCase()
  if (lower === prefix || lower.startsWith(`${prefix}/`)) {
    return path
  }
  return `/${locale}${path}`
}

// Assets shared by more than one front end live in the api service's `public/shared/`, which every
// property can address off its own `API_SERVICE_URL`: the api server mounts `public/` at `/static`
// in every environment, and nginx aliases the same prefix to the merged asset directory in deployed
// ones.
//
// Single-sourcing an asset is what lets every property emit a byte-identical URL for it — required
// for the `Organization` logo, which shares an `@id` across properties. The `shared/` subdirectory
// matters: the deployed directory is a flat merge of several services' `public/` dirs, so same-named
// files at the root shadow each other.
const SHARED_ASSET_PREFIX = '/static/shared'

export function sharedAssetUrl(apiServiceUrl: string, filename: string): string {
  return `${stripTrailingSlash(apiServiceUrl)}${SHARED_ASSET_PREFIX}/${filename}`
}
